import React from 'react';
import {useLocation} from 'react-router-dom';
import api from '../api';
import {useReservation} from '../context/reservation';
import {useComputedReservationDetails} from '../context/computedReservationDetails';
import {LocationStates} from '../views/VerifyModalScreen/VerifyModalScreen';
import {GlobalLocationTypes} from '../utils/types';
import {
  AdditionalGuestDataTypes,
  buildCustomFields,
  buildGuest,
  getHasSomeCustomFields,
  setStoredGuestId,
} from '../utils/guests';

async function createGuest(formData: any, guestData: AdditionalGuestDataTypes) {
  const payload = await buildGuest(formData, guestData);
  return api.guests.post(payload);
}

async function saveCustomFields(formId: string, guestId: string, formData: any) {
  const customFieldsPayload = await buildCustomFields(formData);
  const payload = {
    data: customFieldsPayload,
    guest_id: guestId,
    form_id: formId,
  };

  return api.guestForms.createFields(payload);
}

function useRegisterGuest() {
  const location = useLocation<GlobalLocationTypes>();
  const {shortCustomForm} = useComputedReservationDetails();
  const {data: reservation} = useReservation();

  const getResultFormData = React.useCallback(
    (formData: any) => {
      return {
        ...location.state?.formData,
        ...formData,
      };
    },
    [location.state],
  );

  const getPayloadIdentityVerification = React.useCallback(() => {
    return {
      front_side_scan: location.state?.[LocationStates.frontSideDocumentPhoto]?.picture,
      back_side_scan: location.state?.[LocationStates.backSideDocumentPhoto]?.picture,
      document_passed:
        location.state?.[LocationStates.identityVerificationState]?.documentsPassed,
      biomatch_doc: location.state?.[LocationStates.frontSideDocumentPhoto]?.picture,
      biomatch_selfie: location.state?.[LocationStates.selfiePhoto]?.picture,
      biomatch_passed:
        location.state?.[LocationStates.identityVerificationState]?.biomatchPassed,
    };
  }, [location.state]);

  const getResultGuestData = React.useCallback(
    (guestData: AdditionalGuestDataTypes): AdditionalGuestDataTypes => {
      return {
        reservation,
        customFormId: location.state?.customFormId,
        signature: location.state?.signature,
        isIdOrPassportRequired: Boolean(shortCustomForm?.required_id_or_passport),
        ...getPayloadIdentityVerification(),
        ...guestData,
      };
    },
    [
      getPayloadIdentityVerification,
      location.state?.customFormId,
      location.state?.signature,
      reservation,
      shortCustomForm?.required_id_or_passport,
    ],
  );

  const registerGuest = React.useCallback(
    async ({
      formData = {},
      guestData = {},
    }: {
      formData?: any;
      guestData?: AdditionalGuestDataTypes;
    }) => {
      const resultFormData = getResultFormData(formData);
      const resultGuestData = getResultGuestData(guestData);
      const formId = shortCustomForm?.id || '';
      const hasCustomFields = getHasSomeCustomFields(resultFormData) && formId;

      const guestResult = await createGuest(resultFormData, resultGuestData);

      if (!guestResult.error) {
        setStoredGuestId(guestResult.data?.id);
      }

      if (guestResult.error || !hasCustomFields) {
        return guestResult;
      }

      if (formId) {
        const guestId = guestResult.data?.id;
        const customFieldsResult = await saveCustomFields(
          formId,
          guestId,
          resultFormData,
        );

        if (customFieldsResult.error) {
          return customFieldsResult;
        }
      }

      return guestResult;
    },
    [getResultFormData, getResultGuestData, shortCustomForm],
  );

  return registerGuest;
}

export {useRegisterGuest};
